/**
 * Door Staff Scanner - Full Screen QR Scanner Interface
 * For door staff to scan and check in attendees
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Scan,
  Users,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Camera,
  CameraOff,
  Shield,
  ShieldCheck,
  ShieldAlert,
  IdCard,
  ChevronDown,
  RefreshCw,
  LogOut,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Deny reason options
const DENY_REASONS = [
  "Fake ID",
  "Underage",
  "Intoxicated",
  "Not on list",
  "Banned from venue",
  "Dress code violation",
  "Capacity reached",
  "Other"
];

export default function DoorStaffScanner() {
  const navigate = useNavigate();
  const { session, signOut } = useAuth();
  
  // Assignment state
  const [assignment, setAssignment] = useState(null);
  const [loadingAssignment, setLoadingAssignment] = useState(true);
  const [noAssignment, setNoAssignment] = useState(false);
  
  // Scanner state
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);
  
  // Stats state
  const [stats, setStats] = useState({ total: 0, checked_in: 0 });
  
  // Scan result state
  const [scanResult, setScanResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Recent scans
  const [recentScans, setRecentScans] = useState([]);
  
  // Deny modal state
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  
  // Fetch assignment on mount
  useEffect(() => {
    const fetchAssignment = async () => {
      if (!session?.access_token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/door-staff/assignment`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.assignment) {
            setAssignment(data.assignment);
            setNoAssignment(false);
          } else {
            setNoAssignment(true);
          }
        }
      } catch (err) {
        console.error('Failed to fetch assignment:', err);
        setNoAssignment(true);
      } finally {
        setLoadingAssignment(false);
      }
    };
    
    fetchAssignment();
  }, [session?.access_token]);
  
  // Fetch stats periodically
  useEffect(() => {
    if (!assignment?.event_id || !session?.access_token) return;
    
    const fetchStats = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/door-staff/event/${assignment.event_id}/stats`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    
    fetchStats();
    const interval = setInterval(fetchStats, 10000); // Refresh every 10 seconds
    
    return () => clearInterval(interval);
  }, [assignment?.event_id, session?.access_token]);
  
  // Fetch recent scans
  const fetchRecentScans = useCallback(async () => {
    if (!assignment?.event_id || !session?.access_token) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/door-staff/event/${assignment.event_id}/recent-scans?limit=5`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setRecentScans(data.scans || []);
      }
    } catch (err) {
      console.error('Failed to fetch recent scans:', err);
    }
  }, [assignment?.event_id, session?.access_token]);
  
  useEffect(() => {
    fetchRecentScans();
  }, [fetchRecentScans]);
  
  // Start camera scanner
  const startScanner = useCallback(async () => {
    if (!scannerContainerRef.current || html5QrCodeRef.current) return;
    
    try {
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;
      
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 280, height: 280 },
          aspectRatio: 1.0
        },
        async (decodedText) => {
          // Pause scanner while processing
          await html5QrCode.pause();
          handleScan(decodedText);
        },
        () => {} // Ignore errors from failed scans
      );
      
      setScanning(true);
      setCameraError(null);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err.message || 'Failed to access camera');
      setScanning(false);
    }
  }, []);
  
  // Stop scanner
  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setScanning(false);
  }, []);
  
  // Resume scanner after handling result
  const resumeScanner = useCallback(async () => {
    setScanResult(null);
    setShowDenyModal(false);
    setDenyReason('');
    
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.resume();
      } catch (err) {
        // If resume fails, restart
        await stopScanner();
        await startScanner();
      }
    } else {
      await startScanner();
    }
  }, [startScanner, stopScanner]);
  
  // Start scanner when assignment loads
  useEffect(() => {
    if (assignment && !loadingAssignment) {
      startScanner();
    }
    
    return () => {
      stopScanner();
    };
  }, [assignment, loadingAssignment, startScanner, stopScanner]);
  
  // Handle QR scan
  const handleScan = async (qrData) => {
    if (!session?.access_token) return;
    
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/door-staff/scan?qr_data=${encodeURIComponent(qrData)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      const result = await response.json();
      setScanResult(result);
      
      // Play sound feedback
      if (result.valid) {
        playSound('success');
      } else {
        playSound('error');
      }
    } catch (err) {
      console.error('Scan error:', err);
      setScanResult({
        valid: false,
        error: 'network_error',
        message: 'Network error - please try again'
      });
      playSound('error');
    } finally {
      setProcessing(false);
    }
  };
  
  // Play audio feedback
  const playSound = (type) => {
    try {
      const audio = new Audio(type === 'success' 
        ? 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleVEvg7L+4K1zOwiS1f3stX5LF5XU/PKqbzITpeDz4oxJELHn8c56MBWy5uvCaS0auu3ry3I9K7/t5dGBUDvD7eDZlV9LyO7f3qBsWs3x3eSsgWnd8drjroN14vHZ46eBeeXy1t6hfH7o9NTbn3yB6/TT2Zl7g+321NiWeYTv99TYlHmE7/fU2JR5hO/31NiUeYTv99TYlHmE7/fU2JR5hO/31NiUeQ==' 
        : 'data:audio/wav;base64,UklGRrwGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQwGAAB/goGBgH9+fXx7enl4d3Z1dHNycXBvbm1sa2ppaGdmZWRjYmFgX15dXFtaWVhXVlVUU1JRUE9OTUxLSklIR0ZFRENGR0hJSktMTU5PUFFSU1RVVldYWVpbXF1eX2BhYmNkZWZnaGlqa2xtbm9wcXJzdHV2d3h5ent8fX5/gIGCg4SFhoeIiYqLjI2Oj5CRkpOUlZaXmJmam5ydnp+goaKjpKWmp6ipqqusra6vsLGys7S1tre4ubq7vL2+v8DBwsPExcbHyMnKy8zNzs/Q0dLT1NXW19jZ2tvc3d7f4OHi4+Tl5ufo6err7O3u7/Dx8vP09fb3+Pn6+/z9/v8='
      );
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (err) {
      // Ignore audio errors
    }
  };
  
  // Handle check-in
  const handleCheckIn = async () => {
    if (!scanResult?.pass_id || !session?.access_token) return;
    
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/door-staff/check-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ pass_id: scanResult.pass_id })
      });
      
      const result = await response.json();
      
      if (result.success) {
        playSound('success');
        // Update stats
        setStats(prev => ({ ...prev, checked_in: prev.checked_in + 1 }));
        // Add to recent scans
        setRecentScans(prev => [{
          pass_id: scanResult.pass_id,
          name: scanResult.attendee?.name,
          attendee_type: scanResult.attendee?.attendee_type,
          checked_in_at: result.checked_in_at
        }, ...prev.slice(0, 4)]);
        // Resume scanner after delay
        setTimeout(resumeScanner, 1500);
      } else {
        playSound('error');
        setScanResult({
          valid: false,
          error: 'check_in_failed',
          message: result.error || 'Failed to check in'
        });
      }
    } catch (err) {
      console.error('Check-in error:', err);
      playSound('error');
    } finally {
      setProcessing(false);
    }
  };
  
  // Handle deny entry
  const handleDenyEntry = async () => {
    if (!scanResult?.event_attendee_id || !denyReason || !session?.access_token) return;
    
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/door-staff/deny-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          event_attendee_id: scanResult.event_attendee_id,
          reason: denyReason
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setShowDenyModal(false);
        setTimeout(resumeScanner, 1000);
      }
    } catch (err) {
      console.error('Deny entry error:', err);
    } finally {
      setProcessing(false);
    }
  };
  
  // Handle manual ID check
  const handleManualIdCheck = async () => {
    if (!scanResult?.event_attendee_id || !session?.access_token) return;
    
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/door-staff/manual-id-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ event_attendee_id: scanResult.event_attendee_id })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Update scan result to show verified
        setScanResult(prev => ({
          ...prev,
          attendee: {
            ...prev.attendee,
            age_verified: true,
            manual_id_checked: true
          }
        }));
        playSound('success');
      }
    } catch (err) {
      console.error('Manual ID check error:', err);
    } finally {
      setProcessing(false);
    }
  };
  
  // Handle sign out
  const handleSignOut = async () => {
    await stopScanner();
    await signOut();
    navigate('/login');
  };
  
  // Format time
  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };
  
  // Loading state
  if (loadingAssignment) {
    return (
      <div className="fixed inset-0 bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-electric-ice-blue animate-spin mx-auto mb-4" />
          <p className="text-cloud-white text-lg">Loading assignment...</p>
        </div>
      </div>
    );
  }
  
  // No assignment state
  if (noAssignment) {
    return (
      <div className="fixed inset-0 bg-obsidian flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-coral-red/10 flex items-center justify-center mx-auto mb-6">
            <CameraOff className="w-10 h-10 text-coral-red" />
          </div>
          <h1 className="text-2xl font-bold text-cloud-white mb-4">
            No Active Event Assignment
          </h1>
          <p className="text-soft-silver mb-8">
            You don't have an active door staff assignment. Please contact your event organizer to be assigned to an event.
          </p>
          <Button
            variant="outline"
            onClick={handleSignOut}
            className="border-white/20 text-cloud-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>
    );
  }
  
  const event = assignment?.events || {};
  
  return (
    <div className="fixed inset-0 bg-obsidian flex flex-col" data-testid="door-staff-scanner">
      {/* Top Bar */}
      <div className="flex-shrink-0 bg-graphite/80 backdrop-blur-sm border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Event Name */}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-cloud-white truncate">
              {event.event_name || 'Event Scanner'}
            </h1>
            <p className="text-xs text-soft-silver">Door Staff Mode</p>
          </div>
          
          {/* Live Stats */}
          <div className="flex items-center gap-4 ml-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-electric-ice-blue font-mono">
                {stats.checked_in}
              </div>
              <div className="text-xs text-soft-silver">Checked In</div>
            </div>
            <div className="text-soft-silver">/</div>
            <div className="text-center">
              <div className="text-2xl font-bold text-cloud-white font-mono">
                {stats.total}
              </div>
              <div className="text-xs text-soft-silver">Total</div>
            </div>
          </div>
          
          {/* Sign Out */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSignOut}
            className="ml-4 text-soft-silver hover:text-cloud-white"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {/* Scanner Area */}
      <div className="flex-1 relative overflow-hidden">
        {/* QR Reader Container */}
        <div 
          id="qr-reader" 
          ref={scannerContainerRef}
          className="absolute inset-0"
          style={{ background: '#000' }}
        />
        
        {/* Camera Error Overlay */}
        {cameraError && (
          <div className="absolute inset-0 flex items-center justify-center bg-obsidian/90 p-6">
            <div className="text-center">
              <CameraOff className="w-16 h-16 text-coral-red mx-auto mb-4" />
              <h2 className="text-xl font-bold text-cloud-white mb-2">Camera Error</h2>
              <p className="text-soft-silver mb-6">{cameraError}</p>
              <Button onClick={startScanner}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          </div>
        )}
        
        {/* Processing Overlay */}
        {processing && !scanResult && (
          <div className="absolute inset-0 flex items-center justify-center bg-obsidian/80">
            <Loader2 className="w-12 h-12 text-electric-ice-blue animate-spin" />
          </div>
        )}
        
        {/* Scan Result Card */}
        {scanResult && (
          <div className="absolute inset-0 flex items-center justify-center bg-obsidian/95 p-4">
            <div className="w-full max-w-md">
              {scanResult.valid ? (
                // Valid scan - show attendee details
                <div className="bg-graphite rounded-2xl border border-white/10 overflow-hidden">
                  {/* Header */}
                  <div className={cn(
                    "p-4 border-b border-white/10",
                    scanResult.attendee?.attendee_type === 'member' ? "bg-electric-ice-blue/10" :
                    scanResult.attendee?.attendee_type === 'alumni' ? "bg-soft-amber/10" :
                    "bg-violet-500/10"
                  )}>
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className={cn(
                        "w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold",
                        scanResult.attendee?.avatar_url ? "" : (
                          scanResult.attendee?.attendee_type === 'member' ? "bg-electric-ice-blue/20 text-electric-ice-blue" :
                          scanResult.attendee?.attendee_type === 'alumni' ? "bg-soft-amber/20 text-soft-amber" :
                          "bg-violet-500/20 text-violet-400"
                        )
                      )}>
                        {scanResult.attendee?.avatar_url ? (
                          <img 
                            src={scanResult.attendee.avatar_url} 
                            alt="" 
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          scanResult.attendee?.name?.charAt(0)?.toUpperCase() || '?'
                        )}
                      </div>
                      
                      {/* Name & Type */}
                      <div className="flex-1 min-w-0">
                        <h2 className="text-xl font-bold text-cloud-white truncate">
                          {scanResult.attendee?.name || 'Unknown'}
                        </h2>
                        <Badge className={cn(
                          "mt-1",
                          scanResult.attendee?.attendee_type === 'member' ? "bg-electric-ice-blue/20 text-electric-ice-blue" :
                          scanResult.attendee?.attendee_type === 'alumni' ? "bg-soft-amber/20 text-soft-amber" :
                          "bg-violet-500/20 text-violet-400"
                        )}>
                          {scanResult.attendee?.attendee_type === 'member' ? 'Member' :
                           scanResult.attendee?.attendee_type === 'alumni' ? 'Alumni' : 'External'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {/* Age Verification Status */}
                  {scanResult.event?.age_verification_required && (
                    <div className="p-4 border-b border-white/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {scanResult.attendee?.age_verified ? (
                            <>
                              <ShieldCheck className="w-5 h-5 text-cool-mint" />
                              <span className="text-cool-mint font-medium">Age Verified</span>
                            </>
                          ) : (
                            <>
                              <ShieldAlert className="w-5 h-5 text-coral-red" />
                              <span className="text-coral-red font-medium">Age Not Verified</span>
                            </>
                          )}
                        </div>
                        
                        {!scanResult.attendee?.age_verified && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleManualIdCheck}
                            disabled={processing}
                            className="border-soft-amber text-soft-amber hover:bg-soft-amber/10"
                          >
                            <IdCard className="w-4 h-4 mr-2" />
                            Check ID
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="p-4 space-y-3">
                    <Button
                      size="lg"
                      className="w-full h-16 text-lg bg-cool-mint hover:bg-cool-mint/90 text-obsidian"
                      onClick={handleCheckIn}
                      disabled={processing}
                    >
                      {processing ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle2 className="w-6 h-6 mr-3" />
                          Check In
                        </>
                      )}
                    </Button>
                    
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full h-16 text-lg border-coral-red text-coral-red hover:bg-coral-red/10"
                      onClick={() => setShowDenyModal(true)}
                      disabled={processing}
                    >
                      <XCircle className="w-6 h-6 mr-3" />
                      Deny Entry
                    </Button>
                    
                    <Button
                      variant="ghost"
                      className="w-full text-soft-silver"
                      onClick={resumeScanner}
                    >
                      Cancel & Scan Next
                    </Button>
                  </div>
                </div>
              ) : (
                // Invalid scan - show error
                <div className="bg-graphite rounded-2xl border border-coral-red/30 p-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-coral-red/10 flex items-center justify-center mx-auto mb-4">
                    {scanResult.error === 'already_checked_in' ? (
                      <CheckCircle2 className="w-8 h-8 text-soft-amber" />
                    ) : (
                      <XCircle className="w-8 h-8 text-coral-red" />
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-cloud-white mb-2">
                    {scanResult.error === 'already_checked_in' ? 'Already Checked In' :
                     scanResult.error === 'entry_denied' ? 'Entry Denied' :
                     'Invalid QR Code'}
                  </h2>
                  <p className="text-soft-silver mb-6">{scanResult.message}</p>
                  {scanResult.checked_in_at && (
                    <p className="text-xs text-muted-text mb-4">
                      Checked in at {formatTime(scanResult.checked_in_at)}
                    </p>
                  )}
                  <Button onClick={resumeScanner} className="w-full">
                    Scan Next
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Deny Modal */}
        {showDenyModal && (
          <div className="absolute inset-0 flex items-center justify-center bg-obsidian/95 p-4 z-50">
            <div className="w-full max-w-md bg-graphite rounded-2xl border border-white/10 p-6">
              <h2 className="text-xl font-bold text-cloud-white mb-4">
                Select Deny Reason
              </h2>
              <div className="space-y-2 mb-6">
                {DENY_REASONS.map((reason) => (
                  <button
                    key={reason}
                    onClick={() => setDenyReason(reason)}
                    className={cn(
                      "w-full p-3 rounded-lg text-left transition-colors",
                      denyReason === reason
                        ? "bg-coral-red/20 border border-coral-red/50 text-coral-red"
                        : "bg-obsidian/50 border border-white/10 text-soft-silver hover:bg-obsidian"
                    )}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  className="flex-1"
                  onClick={() => {
                    setShowDenyModal(false);
                    setDenyReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-coral-red hover:bg-coral-red/90"
                  onClick={handleDenyEntry}
                  disabled={!denyReason || processing}
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Confirm Deny'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Recent Scans Strip */}
      {recentScans.length > 0 && !scanResult && (
        <div className="flex-shrink-0 bg-graphite/80 backdrop-blur-sm border-t border-white/10 px-4 py-3">
          <div className="flex items-center gap-2 text-xs text-soft-silver mb-2">
            <Clock className="w-3 h-3" />
            <span>Recent Check-ins</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {recentScans.map((scan, index) => (
              <div
                key={`${scan.pass_id}-${index}`}
                className="flex-shrink-0 px-3 py-2 bg-obsidian/50 rounded-lg border border-white/5"
              >
                <div className="text-sm font-medium text-cloud-white truncate max-w-[120px]">
                  {scan.name}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={cn(
                    "text-[10px] px-1.5 py-0",
                    scan.attendee_type === 'member' ? "bg-electric-ice-blue/20 text-electric-ice-blue" :
                    scan.attendee_type === 'alumni' ? "bg-soft-amber/20 text-soft-amber" :
                    "bg-violet-500/20 text-violet-400"
                  )}>
                    {scan.attendee_type}
                  </Badge>
                  <span className="text-[10px] text-muted-text">
                    {formatTime(scan.checked_in_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
